import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, Globe, FileText, User, Briefcase, ClipboardList } from "lucide-react";

interface ShareTypeConfigProps {
  shareType: string;
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}

export function ShareTypeConfig({ shareType, config, onChange }: ShareTypeConfigProps) {
  const updateConfig = (key: string, value: any) => {
    onChange({ ...config, [key]: value });
  };

  const renderClientPortalConfig = () => (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Select which sections to include in the client portal view
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            <Label>SAIO Analysis</Label>
          </div>
          <Switch
            checked={config.include_saio ?? true}
            onCheckedChange={(v) => updateConfig('include_saio', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <Label>Lead Analytics</Label>
          </div>
          <Switch
            checked={config.include_leads ?? true}
            onCheckedChange={(v) => updateConfig('include_leads', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            <Label>Business Metrics</Label>
          </div>
          <Switch
            checked={config.include_metrics ?? true}
            onCheckedChange={(v) => updateConfig('include_metrics', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            <Label>Project Progress</Label>
          </div>
          <Switch
            checked={config.include_progress ?? true}
            onCheckedChange={(v) => updateConfig('include_progress', v)}
          />
        </div>
      </div>
    </div>
  );

  const renderSiteAuditConfig = () => (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Configure site audit report settings
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Include SEO Score</Label>
          <Switch
            checked={config.include_seo_score ?? true}
            onCheckedChange={(v) => updateConfig('include_seo_score', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Include Technical Issues</Label>
          <Switch
            checked={config.include_technical ?? true}
            onCheckedChange={(v) => updateConfig('include_technical', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Include Recommendations</Label>
          <Switch
            checked={config.include_recommendations ?? true}
            onCheckedChange={(v) => updateConfig('include_recommendations', v)}
          />
        </div>
      </div>
    </div>
  );

  const renderPersonalCardConfig = () => (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Configure personal card display settings
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Show Contact Info</Label>
          <Switch
            checked={config.show_contact ?? true}
            onCheckedChange={(v) => updateConfig('show_contact', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Show Product Interests</Label>
          <Switch
            checked={config.show_products ?? true}
            onCheckedChange={(v) => updateConfig('show_products', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Show Action Buttons</Label>
          <Switch
            checked={config.show_actions ?? true}
            onCheckedChange={(v) => updateConfig('show_actions', v)}
          />
        </div>
      </div>
    </div>
  );

  const renderPdfConfig = () => (
    <div className="space-y-4">
      <Alert>
        <FileText className="h-4 w-4" />
        <AlertDescription>
          Configure PDF document sharing settings
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Apply Watermark</Label>
          <Switch
            checked={config.apply_watermark ?? true}
            onCheckedChange={(v) => updateConfig('apply_watermark', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Track Views</Label>
          <Switch
            checked={config.track_views ?? true}
            onCheckedChange={(v) => updateConfig('track_views', v)}
          />
        </div>
      </div>
    </div>
  );

  const renderProposalConfig = () => (
    <div className="space-y-4">
      <Alert>
        <Briefcase className="h-4 w-4" />
        <AlertDescription>
          Configure sales proposal sharing settings
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Allow Comments</Label>
          <Switch
            checked={config.allow_comments ?? false}
            onCheckedChange={(v) => updateConfig('allow_comments', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Show Pricing</Label>
          <Switch
            checked={config.show_pricing ?? true}
            onCheckedChange={(v) => updateConfig('show_pricing', v)}
          />
        </div>

        <div className="flex items-center justify-between p-3 border rounded-lg">
          <Label>Track Opens</Label>
          <Switch
            checked={config.track_opens ?? true}
            onCheckedChange={(v) => updateConfig('track_opens', v)}
          />
        </div>
      </div>
    </div>
  );

  switch (shareType) {
    case 'client_portal':
      return renderClientPortalConfig();
    case 'site_audit':
      return renderSiteAuditConfig();
    case 'personal_card':
      return renderPersonalCardConfig();
    case 'pdf_access':
      return renderPdfConfig();
    case 'proposal':
      return renderProposalConfig();
    default:
      return (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Select a share type to configure its settings
          </AlertDescription>
        </Alert>
      );
  }
}
